/**
 * Date Utility Functions
 * Standardized date formatting for the entire application using DD/MM/YY format
 */

/**
 * Format date for display in DD/MM/YY format
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
};

/**
 * Format date for display in DD/MM/YYYY format (full year)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateLong = (date) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Format date for HTML input fields (YYYY-MM-DD)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string for input fields
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  // Use local date parts to avoid timezone issues
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Format date range for display
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {string} Formatted date range string
 */
export const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  
  const start = formatDateLong(startDate);
  const end = formatDateLong(endDate);
  
  if (!start || !end) return '';
  
  return `${start} - ${end}`;
};

/**
 * Get current date formatted for input fields
 * @returns {string} Current date in YYYY-MM-DD format
 */
export const getCurrentDateForInput = () => {
  return formatDateForInput(new Date());
};

/**
 * Get current date formatted for display
 * @returns {string} Current date in DD/MM/YY format
 */
export const getCurrentDate = () => {
  return formatDate(new Date());
};

/**
 * Get current date formatted for display (long format)
 * @returns {string} Current date in DD/MM/YYYY format
 */
export const getCurrentDateLong = () => {
  return formatDateLong(new Date());
};

/**
 * Get date N days from now formatted for input
 * @param {number} days - Number of days to add
 * @returns {string} Future date in YYYY-MM-DD format
 */
export const getFutureDateForInput = (days) => {
  const futureDate = new Date();
  futureDate.setDate(futureDate.getDate() + days);
  return formatDateForInput(futureDate);
};

/**
 * Check if a date string is valid
 * @param {string} dateString - Date string to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidDate = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Calculate duration in weeks between two dates
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {number} Duration in weeks (rounded up)
 */
export const getDurationInWeeks = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.ceil(diffDays / 7);
  
  return diffWeeks;
}; 